import { formatCompactPercent, formatNumber } from '@/shared/utils/format-number';

import type { CampaignOverview } from '@/entities/campaign';
import type { CampaignMetrics } from '@/entities/campaign/api/fetch-campaign-metrics';
import { useCampaignMetrics } from '@/entities/campaign/query/use-campaign-metrics';
import { useCampaignOverview } from '@/entities/campaign/query/use-campaign-overview';
import { Button } from '@/shared/components/shadcn/button';
import { Stage } from '@/shared/components/stage/stage';
import { RECENT_POSTS_TOOLTIP } from '../../constants';
import {
  EmptyInlineState,
  OverviewMetricSection,
  OverviewShortcutCard,
  SummaryStatCard,
} from '../campaign-detail-primitives';
import { CampaignOverviewSkeleton } from '../campaign-overview-skeleton';

const EMPTY_OVERVIEW: CampaignOverview = {
  campaign_creators_count: 0,
  campaign_creators_with_post_count: 0,
  tracked_posts_count: 0,
  recent_posts_count: 0,
};

const EMPTY_METRICS: CampaignMetrics = {
  average_engagement_rate: 0,
  total_comments: 0,
  total_engagements: 0,
  total_likes: 0,
  total_shared: 0,
  total_views: 0,
};

export function CampaignDetailOverviewTab({
  campaignId,
  onJumpToAllPosts,
  onJumpToMissingCreators,
  onJumpToRecentPosts,
}: {
  campaignId: string;
  onJumpToAllPosts: () => void;
  onJumpToMissingCreators: () => void;
  onJumpToRecentPosts: () => void;
}) {
  const campaignSummaryQuery = useCampaignOverview(campaignId);
  const campaignMetricsQuery = useCampaignMetrics(campaignId);
  const isLoading = campaignSummaryQuery.isLoading || campaignMetricsQuery.isLoading;
  const isError = campaignSummaryQuery.isError || campaignMetricsQuery.isError;
  const overview = campaignSummaryQuery.data ?? EMPTY_OVERVIEW;
  const metrics = campaignMetricsQuery.data ?? EMPTY_METRICS;
  const missingCreatorCount =
    overview.campaign_creators_count - overview.campaign_creators_with_post_count;
  const onRetry = () => {
    void campaignSummaryQuery.refetch();
    void campaignMetricsQuery.refetch();
  };

  return (
    <Stage>
      <Stage.Scene status={isError}>
        <EmptyInlineState
          title="Overview is unavailable"
          description="We could not load campaign summary metrics right now."
          action={
            <Button variant="outline" size="sm" onClick={onRetry}>
              Retry
            </Button>
          }
        />
      </Stage.Scene>
      <Stage.Scene status={isLoading}>
        <CampaignOverviewSkeleton />
      </Stage.Scene>
      <Stage.Scene default>
        <div className="flex h-full min-h-0 flex-col gap-6">
          <div className="space-y-6">
            <section className="space-y-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-foreground text-xl leading-6 font-semibold">
                    Needs action now
                  </h2>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-3">
                <OverviewShortcutCard
                  actionLabel={
                    missingCreatorCount > 0
                      ? 'View creators awaiting posts'
                      : 'Open creator coverage'
                  }
                  description={
                    missingCreatorCount > 0 ? (
                      missingCreatorCount === 1 ? (
                        <span>
                          <span className="text-destructive font-semibold">1 creator</span>{' '}
                          hasn&apos;t posted yet
                        </span>
                      ) : (
                        <span>
                          <span className="text-destructive font-semibold">
                            {missingCreatorCount} creators
                          </span>{' '}
                          haven&apos;t posted yet
                        </span>
                      )
                    ) : (
                      'Everyone on the roster has at least one tracked post.'
                    )
                  }
                  label="Creator coverage"
                  onAction={onJumpToMissingCreators}
                  showDescription
                  value={`${overview.campaign_creators_with_post_count} out of ${overview.campaign_creators_count}`}
                />

                <OverviewShortcutCard
                  actionLabel={
                    overview.recent_posts_count > 0 ? 'Review recent posts' : 'Open post feed'
                  }
                  description={
                    overview.recent_posts_count > 0
                      ? `${overview.recent_posts_count} recent post${overview.recent_posts_count === 1 ? '' : 's'} collected.`
                      : 'No recent posts have been collected yet.'
                  }
                  infoTooltip={RECENT_POSTS_TOOLTIP}
                  label="Recently collected"
                  onAction={onJumpToRecentPosts}
                  value={formatNumber(overview.recent_posts_count)}
                />

                <OverviewShortcutCard
                  actionLabel="Open all tracked posts"
                  description="Review the full content set and spot anything that should be excluded."
                  label="Tracked posts"
                  onAction={onJumpToAllPosts}
                  value={formatNumber(overview.tracked_posts_count)}
                />
              </div>
            </section>

            <OverviewMetricSection title="Performance">
              <SummaryStatCard label="Total Views" value={formatNumber(metrics.total_views)} />
              <SummaryStatCard
                label="Total Engagements"
                value={formatNumber(metrics.total_engagements)}
                infoTooltip="Calculated as likes + comments + shares across tracked posts."
              />
              <SummaryStatCard
                label="Average ER"
                value={formatCompactPercent(metrics.average_engagement_rate * 100)}
                infoTooltip="Average engagement rate across tracked posts. ER = (likes + comments + shares) / views."
              />
              <SummaryStatCard label="Total Likes" value={formatNumber(metrics.total_likes)} />
              <SummaryStatCard
                label="Total Comments"
                value={formatNumber(metrics.total_comments)}
              />
              <SummaryStatCard label="Total Shares" value={formatNumber(metrics.total_shared)} />
            </OverviewMetricSection>
          </div>
        </div>
      </Stage.Scene>
    </Stage>
  );
}
